import React from 'react';
import { Section } from './ui/Section';
import { FadeIn } from './ui/FadeIn';
export function StoryAndLegacy() {
  const milestones = [{
    year: '2004',
    title: 'Began Executive Recruitment',
    desc: 'Started a journey in high-stakes talent acquisition.'
  }, {
    year: '2011',
    title: 'First C-Suite Placement',
    desc: 'Successfully placed a CEO for a Fortune 500 subsidiary.'
  }, {
    year: '2015',
    title: 'Top Advisor Recognition',
    desc: 'Named among the premier talent advisors in the Southwest.'
  }, {
    year: '2018',
    title: 'Founded Boardline',
    desc: "Established a firm dedicated to the candidate's perspective."
  }, {
    year: '2023',
    title: '500+ Careers Transformed',
    desc: 'Reaching a landmark in executive career impact.'
  }];
  return <Section background="warm" id="about" className="relative" data-id="element-371">
      {/* Subtle decorative accent */}
      <div className="absolute top-0 right-0 w-72 h-72 bg-accent-400/[0.03] rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none" data-id="element-372" />

      {/* Unified section header */}
      <FadeIn className="text-center mb-16" data-id="element-373">
        <span className="text-accent-500 text-xs font-bold uppercase tracking-[0.25em] block mb-3" data-id="element-374">
          Who We Are
        </span>
        <h2 className="text-3xl md:text-5xl font-serif text-ink-900 mb-4" data-id="element-375">
          Our Story & Legacy
        </h2>
        <div className="h-[2px] w-16 bg-accent-400 mx-auto" data-id="element-376" />
      </FadeIn>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 items-stretch relative" data-id="element-377">
        {/* Vertical divider on desktop */}
        <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 -translate-x-1/2" data-id="element-378">
          <div className="w-[1px] h-full bg-gradient-to-b from-transparent via-sage-300 to-transparent" data-id="element-379" />
        </div>

        {/* Left: The Boardline Story */}
        <div className="lg:pr-14 pb-12 lg:pb-0" data-id="element-380">
          <FadeIn data-id="element-381">
            <div className="flex items-center gap-3 mb-6" data-id="element-382">
              <div className="w-10 h-[2px] bg-accent-400" data-id="element-383" />
              <h3 className="text-sm font-bold uppercase tracking-[0.2em] text-accent-500" data-id="element-384">
                The Boardline Story
              </h3>
            </div>
          </FadeIn>

          <FadeIn delay={0.1} data-id="element-385">
            <p className="text-lg text-body leading-relaxed mb-6" data-id="element-386">
              Beginning in the high-pressure world of executive search, our
              leadership team quickly realized that while companies had armies
              of recruiters, the executives themselves—the very talent driving
              the economy—often navigated their most critical career pivots
              alone.
            </p>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-387">
            <p className="text-lg text-body leading-relaxed mb-8" data-id="element-388">
              Boardline Executive Advisory was born from a desire to balance the
              scales. We are not headhunters working for a corporation; we are
              strategic partners working for you.
            </p>
          </FadeIn>

          <FadeIn delay={0.3} data-id="element-389">
            <blockquote className="relative bg-white border border-sage-200 p-8 shadow-sm mb-8" data-id="element-390">
              <div className="absolute -top-3 left-8 w-6 h-6 bg-accent-400 flex items-center justify-center" data-id="element-391">
                <span className="text-white text-lg font-serif leading-none" data-id="element-392">
                  "
                </span>
              </div>
              <p className="text-lg font-serif italic text-ink-800 leading-relaxed mb-4" data-id="element-393">
                True career stewardship is about more than the next role. It is
                about crafting a legacy that aligns with your deepest values and
                highest potential.
              </p>
              <footer className="flex items-center gap-3" data-id="element-394">
                <div className="w-8 h-[1px] bg-accent-400" data-id="element-395" />
                <span className="text-xs font-bold text-ink-900 uppercase tracking-widest" data-id="element-396">
                  Simeon Praymond
                </span>
              </footer>
            </blockquote>
          </FadeIn>

          <FadeIn delay={0.4} data-id="element-397">
            <p className="text-base text-body leading-relaxed" data-id="element-398">
              Today, Boardline serves a select group of senior leaders,
              providing the market intelligence, strategic positioning, and
              discreet access required to secure roles that are not just better,
              but transformative.
            </p>
          </FadeIn>
        </div>

        {/* Mobile divider */}
        <div className="lg:hidden w-full h-[1px] bg-sage-300 my-4" data-id="element-399" />

        {/* Right: Our Legacy Timeline */}
        <div className="lg:pl-14 pt-12 lg:pt-0" data-id="element-400">
          <FadeIn data-id="element-401">
            <div className="flex items-center gap-3 mb-8" data-id="element-402">
              <div className="w-10 h-[2px] bg-accent-400" data-id="element-403" />
              <h3 className="text-sm font-bold uppercase tracking-[0.2em] text-accent-500" data-id="element-404">
                Our Legacy
              </h3>
            </div>
          </FadeIn>

          <div className="relative pl-10" data-id="element-405">
            {/* Timeline line */}
            <div className="absolute left-[11px] top-3 bottom-3 w-[2px] bg-gradient-to-b from-accent-400 via-accent-400/40 to-accent-400/10" data-id="element-406" />

            <div className="space-y-0" data-id="element-407">
              {milestones.map((item, index) => <FadeIn key={index} delay={0.1 + index * 0.08} className="relative" data-id="element-408">
                  {/* Timeline dot */}
                  <div className="absolute -left-10 top-4 z-10" data-id="element-409">
                    <div className="w-[22px] h-[22px] rounded-full bg-sage-100 border-[3px] border-accent-400 shadow-sm" data-id="element-410" />
                  </div>

                  <div className="pb-8 last:pb-0" data-id="element-411">
                    <div className="bg-white border border-sage-200 p-5 shadow-sm hover:shadow-md hover:border-accent-400/30 transition-all duration-300" data-id="element-412">
                      <div className="flex items-baseline gap-3 mb-1.5" data-id="element-413">
                        <span className="text-accent-500 font-bold text-xl font-serif" data-id="element-414">
                          {item.year}
                        </span>
                        <div className="w-4 h-[1px] bg-sage-300" data-id="element-415" />
                      </div>
                      <h4 className="text-ink-900 font-serif text-base font-bold mb-1" data-id="element-416">
                        {item.title}
                      </h4>
                      <p className="text-body-muted text-sm leading-relaxed" data-id="element-417">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                </FadeIn>)}
            </div>
          </div>
        </div>
      </div>
    </Section>;
}